import type { Vec2 } from "../game/math";
import type { CosmeticRenderStyle } from "../theme/theme";
import type { CosmeticDetailLevel, CosmeticMark } from "./cosmetic-geometry";

export interface CosmeticBodyLayer {
  readonly color: string;
  /** Width and offset are fractions of the authoritative snake width. */
  readonly width: number;
  readonly offset: Vec2;
  readonly opacity: number;
}

export type CosmeticPathCommand =
  | readonly ["M" | "L", number, number]
  | readonly ["Q", number, number, number, number]
  | readonly ["C", number, number, number, number, number, number]
  | readonly ["Z"];

interface CosmeticShapeStyle {
  readonly fill?: string;
  readonly stroke?: string;
  readonly strokeWidth?: number;
  readonly opacity?: number;
  readonly role?: "eye" | "motif" | "surface";
}

export type CosmeticShape = CosmeticShapeStyle &
  (
    | {
        readonly kind: "path";
        readonly path: readonly CosmeticPathCommand[];
      }
    | {
        readonly kind: "ellipse";
        readonly center: Vec2;
        readonly radiusX: number;
        readonly radiusY: number;
      }
  );

/** Shared by SVG previews and Canvas: fixed lighting, without a wider silhouette. */
export const cosmeticBodyLayers = (
  style: CosmeticRenderStyle,
  detail: CosmeticDetailLevel,
): readonly CosmeticBodyLayer[] => {
  const layers: CosmeticBodyLayer[] = [
    { color: style.edge, width: 1, offset: { x: 0, y: 0 }, opacity: 1 },
    { color: style.shade, width: 0.92, offset: { x: 0, y: 0.02 }, opacity: 1 },
    {
      color: style.body,
      width: 0.82,
      offset: { x: -0.01, y: -0.04 },
      opacity: 1,
    },
    {
      color: style.plate,
      width: 0.6,
      offset: { x: -0.04, y: -0.1 },
      opacity: 0.8,
    },
  ];
  if (detail === "quality") {
    layers.push(
      {
        color: style.plate,
        width: 0.38,
        offset: { x: -0.055, y: -0.15 },
        opacity: 0.65,
      },
      {
        color: style.node,
        width: 0.07,
        offset: { x: -0.07, y: -0.28 },
        opacity: 0.42,
      },
    );
  }
  return layers;
};

const line = (
  path: readonly CosmeticPathCommand[],
  stroke: string,
  strokeWidth: number,
  opacity = 1,
): CosmeticShape => ({
  kind: "path",
  path,
  stroke,
  strokeWidth,
  opacity,
  role: "motif",
});

const ellipse = (
  x: number,
  y: number,
  radiusX: number,
  radiusY: number,
  fill: string,
  opacity = 1,
): CosmeticShape => ({
  kind: "ellipse",
  center: { x, y },
  radiusX,
  radiusY,
  fill,
  opacity,
});

/** Motif coordinates are normalized to the mark radius and never add body plates. */
export const cosmeticMarkShapes = (
  mark: CosmeticMark,
  style: CosmeticRenderStyle,
): readonly CosmeticShape[] => {
  const side = mark.side * 1.8;
  if (mark.kind === "signal-chevron") {
    const path: readonly CosmeticPathCommand[] = [
      ["M", -0.9, -0.62],
      ["Q", 0.85, 0, -0.9, 0.62],
    ];
    return [
      line(path, style.motifOutline, 0.38, 0.8),
      line(path, style.accent, 0.2),
    ];
  }
  if (mark.kind === "circuit-branch") {
    const path: readonly CosmeticPathCommand[] = [
      ["M", -1, 0],
      ["L", -0.1, 0],
      ["Q", 0.26, 0, 0.42, side * 0.65],
      ["Q", 0.56, side, 1, side],
    ];
    return [
      line(path, style.motifOutline, 0.28),
      line(path, style.accent, 0.13),
      ellipse(1, side, 0.12, 0.12, style.node),
    ];
  }
  if (mark.kind === "ember-spot") {
    return [
      ellipse(-0.22, side, 0.94, 0.64, style.facet, 0.92),
      ellipse(0.7, -side * 0.6, 0.38, 0.3, style.shade, 0.65),
      ellipse(-0.44, side - 0.15, 0.21, 0.1, style.accent, 0.68),
    ];
  }
  if (mark.kind === "constellation-star") {
    const path: readonly CosmeticPathCommand[] = [
      ["M", -1, side],
      ["Q", 0, side - 0.14, 1, side],
      ["M", 0, side - 1],
      ["Q", -0.14, side, 0, side + 1],
    ];
    return [
      line(path, style.motifOutline, 0.4, 0.85),
      line(path, style.accent, 0.18),
      ellipse(0, side, 0.16, 0.16, style.node),
    ];
  }
  if (mark.kind === "prism-facet") {
    return [
      {
        kind: "path",
        path: [
          ["M", 1, 0],
          ["Q", 0.18, 0.2, 0, 0.64],
          ["Q", -0.18, 0.2, -1, 0],
          ["Q", -0.18, -0.2, 0, -0.64],
          ["Q", 0.18, -0.2, 1, 0],
          ["Z"],
        ],
        fill: style.facet,
        stroke: style.accent,
        strokeWidth: 0.12,
        role: "motif",
      },
    ];
  }
  if (mark.kind === "tide-node") {
    return [
      {
        ...ellipse(0, side, 0.84, 0.84, style.body),
        stroke: style.motifOutline,
        strokeWidth: 0.3,
      },
      {
        ...ellipse(0, side, 0.67, 0.67, style.plate),
        stroke: style.accent,
        strokeWidth: 0.22,
      },
      ellipse(-0.14, side - 0.14, 0.2, 0.2, style.node),
    ];
  }
  return [
    ellipse(0, side, 0.95, 0.95, style.motifOutline),
    ellipse(0, side, 0.55, 0.55, style.node),
  ];
};

/** The whole head fits inside a unit collision circle, including its outline. */
export const cosmeticHeadShapes = (
  style: CosmeticRenderStyle,
): readonly CosmeticShape[] => {
  const obsidian = style.pattern === "constellation";
  const shapes: CosmeticShape[] = [
    {
      ...ellipse(0, 0, 0.93, 0.83, obsidian ? style.shade : style.body),
      stroke: style.edge,
      strokeWidth: 0.12,
    },
    ellipse(-0.08, -0.16, 0.71, 0.53, obsidian ? style.edge : style.plate, 0.8),
    line(
      [
        ["M", -0.49, -0.4],
        ["Q", -0.05, -0.65, 0.43, -0.36],
      ],
      style.node,
      0.065,
      0.58,
    ),
  ];
  const motifColor = style.pattern === "plain" ? style.plate : style.accent;
  if (style.pattern === "ember") {
    shapes.push(ellipse(-0.38, 0.07, 0.2, 0.14, style.facet));
  } else if (style.pattern === "constellation") {
    shapes.push({
      kind: "path",
      path: [
        ["M", -0.12, 0.04],
        ["L", -0.37, 0.22],
        ["L", -0.6, 0.04],
        ["L", -0.37, -0.14],
        ["Z"],
      ],
      fill: style.facet,
      stroke: style.node,
      strokeWidth: 0.04,
      role: "motif",
    });
  } else if (style.pattern === "tide" || style.pattern === "circuit") {
    shapes.push({
      ...ellipse(-0.34, 0.03, 0.18, 0.18, style.shade),
      stroke: motifColor,
      strokeWidth: 0.075,
    });
  } else {
    shapes.push(
      line(
        [
          ["M", -0.57, 0.1],
          ["Q", -0.22, 0.17, -0.06, 0.05],
        ],
        motifColor,
        0.11,
      ),
    );
  }
  for (const side of [-1, 1]) {
    shapes.push(
      { ...ellipse(0.35, side * 0.34, 0.155, 0.16, style.node), role: "eye" },
      ellipse(0.395, side * 0.34, 0.085, 0.105, style.shade),
      ellipse(0.42, side * 0.34 - 0.035, 0.025, 0.025, style.node),
    );
  }
  return shapes;
};

export const cosmeticPathData = (
  commands: readonly CosmeticPathCommand[],
): string => commands.map((command) => command.join(" ")).join(" ");

export const traceCosmeticPath = (
  context: CanvasRenderingContext2D,
  commands: readonly CosmeticPathCommand[],
): void => {
  for (const command of commands) {
    if (command[0] === "M") context.moveTo(command[1], command[2]);
    else if (command[0] === "L") context.lineTo(command[1], command[2]);
    else if (command[0] === "Q")
      context.quadraticCurveTo(command[1], command[2], command[3], command[4]);
    else if (command[0] === "C")
      context.bezierCurveTo(
        command[1],
        command[2],
        command[3],
        command[4],
        command[5],
        command[6],
      );
    else context.closePath();
  }
};

export const paintCosmeticShapes = (
  context: CanvasRenderingContext2D,
  shapes: readonly CosmeticShape[],
): void => {
  for (const shape of shapes) {
    context.beginPath();
    context.globalAlpha = shape.opacity ?? 1;
    if (shape.kind === "ellipse") {
      context.ellipse(
        shape.center.x,
        shape.center.y,
        shape.radiusX,
        shape.radiusY,
        0,
        0,
        Math.PI * 2,
      );
    } else traceCosmeticPath(context, shape.path);
    if (shape.fill) {
      context.fillStyle = shape.fill;
      context.fill();
    }
    if (shape.stroke) {
      context.strokeStyle = shape.stroke;
      context.lineWidth = shape.strokeWidth ?? 0.1;
      context.stroke();
    }
  }
  context.globalAlpha = 1;
};
